import React from 'react';
import { Box, Typography, Grid, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Target } from 'lucide-react';

interface KeyResult {
  id: string;
  description: string;
  progress: number;
  target: string;
}

interface Objective {
  id: string;
  title: string;
  description: string;
  keyResults: KeyResult[];
}

interface ObjectivesKeyResultsProps {
  objectives: Objective[];
}

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

const ObjectiveContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const KeyResultContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  background: 'rgba(138, 43, 226, 0.05)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.1)',
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    background: 'linear-gradient(90deg, #00CC99, #8A2BE2)',
  },
}));

const ObjectivesKeyResults: React.FC<ObjectivesKeyResultsProps> = ({ objectives }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Target size={20} color="#8A2BE2" style={{ marginRight: 8 }} />
        <Typography variant="h5" sx={{ color: '#fff' }}>
          Objectives & Key Results
        </Typography>
      </Box>

      <SectionContainer>
        {objectives.map((objective) => (
          <ObjectiveContainer key={objective.id}>
            <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
              {objective.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
              {objective.description}
            </Typography>

            <Grid container spacing={2}>
              {objective.keyResults.map((kr) => (
                <Grid item xs={12} key={kr.id}>
                  <KeyResultContainer>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      {kr.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Progress: {kr.progress}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#00CC99' }}>
                        Target: {kr.target}
                      </Typography>
                    </Box>
                    <StyledLinearProgress variant="determinate" value={kr.progress} />
                  </KeyResultContainer>
                </Grid>
              ))}
            </Grid>
          </ObjectiveContainer>
        ))}
      </SectionContainer>
    </Box>
  );
};

export default ObjectivesKeyResults; 